<template>
  <div class="bg-white text-slate-600">
    <!-- Header -->
    <LandingHeader
      :brand-name="globalConfig.BRAND_NAME || 'HiCHAT'"
      brand-url="/"
      :logo-url="globalConfig.LOGO_THUMBNAIL || ''"
      primary-cta-url="/app/auth/signup"
      :nav-links="navLinks"
    />

    <!-- Main Content -->
    <main class="min-h-screen py-12">
      <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <!-- Loading -->
        <div v-if="loading" class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-hilab"></div>
          <span class="ml-3 text-slate-500">Đang tải...</span>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="text-center py-12">
          <div class="text-red-500 mb-4">
            <LucideIcon name="triangle-alert" :size="48" class="mx-auto" />
          </div>
          <h3 class="text-lg font-medium text-slate-900 mb-2">Không thể tải trang</h3>
          <p class="text-slate-500 mb-4">{{ error }}</p>
          <button @click="retryLoad" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-hilab hover:bg-hilab/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hilab">
            Thử lại
          </button>
        </div>

        <!-- Content -->
        <div v-else-if="pageContent" class="prose prose-lg max-w-none" v-html="pageContent"></div>

  
      </div>
    </main>

    <!-- Footer -->
    <LandingFooter
      :brand-name="globalConfig.BRAND_NAME || 'HiCHAT'"
      brand-url="/"
      :logo-url="globalConfig.LOGO_THUMBNAIL || ''"
      :support-email="supportEmail"
      :content="footerData"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import LandingHeader from '../components/LandingHeader.vue';
import LandingFooter from '../components/LandingFooter.vue';
import LucideIcon from '../../dashboard/components/LucideIcon.vue';

const props = defineProps({
  slug: { type: [String, Array], default: '' },
  pageType: { type: String, default: 'static' },
});

const store = useStore();
const route = useRoute();

const globalConfig = computed(() => store.getters['home/globalConfig'] || {});
const footerData = computed(() => store.getters['home/footerData']);
const supportEmail = computed(() => globalConfig.value.SUPPORT_EMAIL || 'support@hichat.asia');

const navLinks = computed(() => [
  { to: '/', label: 'Trang chủ' },
  { to: '/docs', label: 'Hướng dẫn' },
  { to: '/#pricing', label: 'Bảng giá', hash: true }
]);

// Normalize slug for nested paths
const currentSlug = computed(() => {
  if (Array.isArray(props.slug)) return props.slug.join('/');
  return props.slug || '';
});

// Use docs store state (same as DocsView)
const currentPage = computed(() => store.getters['docs/currentPage']);
const loading = computed(() => store.getters['docs/loading']);
const error = computed(() => store.getters['docs/error']);
const pageContent = computed(() => currentPage.value?.rich_text_content?.body || '');

const loadPage = async () => {
  store.commit('docs/SET_ERROR', null);
  store.commit('docs/CLEAR_CURRENT_PAGE');
  await store.dispatch('docs/fetchPage', { slug: currentSlug.value || 'index', pageType: props.pageType });
};

const retryLoad = async () => {
  await loadPage();
};

onMounted(async () => {
  await store.dispatch('locale/initLocale');
  await store.dispatch('home/fetchContentWithCache');

  if (currentSlug.value) {
    await loadPage();
  }
});

watch(
  () => route.params.slug,
  async newSlug => {
    if (!newSlug) {
      store.commit('docs/CLEAR_CURRENT_PAGE');
      return;
    }
    const slugStr = Array.isArray(newSlug) ? newSlug.join('/') : newSlug;
    const currentPath = currentPage.value?.full_path || currentPage.value?.slug || '';
    if (slugStr !== currentPath) {
      await loadPage();
    }
  },
  { immediate: false }
);
</script>